import type { KeyboardEvent } from 'react'
import type { ExploreLeague } from '../../types'
import './ExploreLeaguesScreen.css'

interface ExploreLeaguesScreenProps {
  query: string
  onQueryChange: (query: string) => void
  results: ExploreLeague[]
  isLoading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onJoin: (league: ExploreLeague) => void
  onSelectLeague: (leagueId: string) => void
  onBack: () => void
}

/**
 * The mini-league directory.
 *
 * A row carries the league name and `N players`, and nothing else. No points, no rank, no leader,
 * no founding year: someone browsing has no standing in any of these leagues, so a standings-shaped
 * row would be showing them a table they are not in. It is a list of rooms, not a leaderboard.
 *
 * Invite-only leagues never reach this component — the server leaves them out of the response, so
 * there is no client-side filter here to get wrong.
 *
 * Only a league you are already in opens its detail screen from here (the out-of-scope
 * note, resolved that way): the detail is built from the leagues you belong to, and a non-member
 * has no standings, no rank and no row in it. The public preview is.
 */

/** The trailing action, decided by the viewer's relationship to the league. */
function RowAction({
  league,
  onJoin,
}: {
  league: ExploreLeague
  onJoin: (league: ExploreLeague) => void
}) {
  if (league.viewerState === 'member') {
    return <span className="explore-row__state">Joined</span>
  }
  if (league.viewerState === 'requested') {
    return <span className="explore-row__state">Requested</span>
  }
  return (
    <button
      type="button"
      className="explore-row__join"
      onClick={(event) => {
        // The row itself opens the league, so the action must not do both.
        event.stopPropagation()
        onJoin(league)
      }}
    >
      {league.joinPolicy === 'open' ? 'Join' : 'Ask to join'}
    </button>
  )
}

function ExploreRow({
  league,
  onJoin,
  onSelect,
}: {
  league: ExploreLeague
  onJoin: (league: ExploreLeague) => void
  onSelect: () => void
}) {
  const canOpen = league.viewerState === 'member'
  const pressable = canOpen
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: onSelect,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSelect()
          }
        },
      }
    : {}

  return (
    <div className="explore-row" {...pressable}>
      <div className="explore-row__identity">
        <p className="explore-row__name">{league.name}</p>
        <p className="explore-row__meta">{`${league.memberCount.toLocaleString()} players`}</p>
      </div>
      <RowAction league={league} onJoin={onJoin} />
    </div>
  )
}

function ExploreLeaguesScreen({
  query,
  onQueryChange,
  results,
  isLoading,
  hasMore,
  onLoadMore,
  onJoin,
  onSelectLeague,
  onBack,
}: ExploreLeaguesScreenProps) {
  // Two different empty states: a search that found nothing is the user's fault to fix, an empty
  // directory is not, and telling someone to refine a search of nothing is a dead end.
  const emptyMessage = query.trim()
    ? 'No leagues match that search.'
    : 'No leagues to explore yet.'

  return (
    <div className="explore-leagues">
      <div className="explore-leagues__topbar">
        <button className="explore-leagues__back" onClick={onBack} aria-label="Back to leagues">
          <span className="explore-leagues__back-chevron" aria-hidden="true">
            ‹
          </span>
          Leagues
        </button>
      </div>

      <p className="explore-leagues__title">Explore leagues</p>

      <input
        type="search"
        className="explore-leagues__search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Search leagues"
        aria-label="Search leagues"
      />

      {results.length > 0 ? (
        <>
          <div className="explore-leagues__rows">
            {results.map((league) => (
              <ExploreRow
                key={league.id}
                league={league}
                onJoin={onJoin}
                onSelect={() => onSelectLeague(league.id)}
              />
            ))}
          </div>
          {hasMore && (
            <button type="button" className="explore-leagues__more" onClick={onLoadMore}>
              Load more
            </button>
          )}
        </>
      ) : (
        // Nothing is said while the first search is still out — an empty state that flashes on
        // every keystroke reads as "no results" for a query that has not been answered yet.
        !isLoading && <p className="explore-leagues__empty">{emptyMessage}</p>
      )}
    </div>
  )
}

export default ExploreLeaguesScreen
